import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Smartphone, Wifi, SignalHigh, ClipboardList } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionLabel, AudienceToggle, HeroesBanner, PromoGrid } from "@/components/att/ATTShared";
import { ATT_SERVICES, ATT_LOGOS, ATT_BLUE } from "@/attData";
import { CONTACT } from "@/data";

const SECTIONS = [
  { to: "/att/devices", label: "Shop Devices", desc: "Phones, tablets & watches — every color, storage size, and price.", Icon: Smartphone },
  { to: "/att/plans", label: "Wireless Plans", desc: "Unlimited plans for homes and teams, with hero and multi-line savings.", Icon: SignalHigh },
  { to: "/att/internet", label: "Internet & Fiber", desc: "AT&T Fiber up to 5 GIG and Internet Air — residential & business pricing.", Icon: Wifi },
  { to: "/att/preorder", label: "Start a Pre-Order", desc: "Lock in your devices, plans, trade-in, and number transfer. We handle the rest.", Icon: ClipboardList },
];

export default function ATTPage() {
  const [tab, setTab] = useState("residential");
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash === "#agency") {
      setTimeout(() => document.getElementById("agency")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  return (
    <div data-testid="att-page">
      {/* HERO */}
      <div className="relative overflow-hidden bg-[#06111f]">
        <img src="/att-hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-right opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06111f] via-[#06111f]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-center gap-3">
            <span className="h-[3px] w-10" style={{ backgroundColor: ATT_BLUE }} />
            <p className="font-body text-xs md:text-sm font-bold tracking-[0.3em] uppercase" style={{ color: ATT_BLUE }}>
              More connection. More possibility.
            </p>
          </div>
          <h1 className="mt-6 font-serif-display font-bold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl" data-testid="att-hero-heading">
            <span className="block text-white">Your life.</span>
            <span className="block text-white">Your business.</span>
            <span className="block" style={{ color: ATT_BLUE }}>Connected.</span>
          </h1>
          <p className="mt-6 max-w-xl font-body font-light text-base md:text-lg text-white/75 leading-relaxed">
            AT&amp;T services for the moments that matter. Explore internet and wireless with Heritage Family
            Protection Agency — your AT&amp;T Authorized Dealer.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              onClick={() => navigate("/att/preorder?aud=residential")}
              data-testid="att-hero-home-btn"
              className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-body text-sm font-bold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
              style={{ backgroundColor: ATT_BLUE }}
            >
              For your home <ArrowUpRight size={16} />
            </button>
            <button
              onClick={() => navigate("/att/preorder?aud=business")}
              data-testid="att-hero-business-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-8 py-4 font-body text-sm font-bold tracking-wide text-white hover:border-white transition-colors duration-300"
            >
              For your business <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <HeroesBanner />

      {/* iPhone 18 SHOWCASE */}
      <div className="bg-[#050505] py-14 md:py-20" data-testid="att-iphone18-showcase">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionLabel>New &amp; Pre-Ordering Now</SectionLabel>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
              We now offer the <span style={{ color: ATT_BLUE }}>iPhone 18 Pro &amp; Pro Max.</span>
            </h2>
            <p className="mt-4 max-w-lg font-body font-light text-base md:text-lg text-white/65 leading-relaxed">
              Four stunning finishes — Black, Silver, Glacier Blue, and Burgundy. Pick your color, storage,
              and installment plan, add trade-in promo pricing, and lock in your pre-order today.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link
                to="/att/devices/iphone-18-pro-max"
                data-testid="att-iphone18-promax-cta"
                className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-body text-sm font-bold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: ATT_BLUE }}
              >
                Customize iPhone 18 Pro Max <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/att/devices/iphone-18-pro"
                data-testid="att-iphone18-pro-cta"
                className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-7 py-3.5 font-body text-sm font-bold tracking-wide text-white hover:border-[#00A8E0] hover:text-[#00A8E0] transition-colors duration-300"
              >
                Customize iPhone 18 Pro <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
          <Link to="/att/devices/iphone-18-pro-max" data-testid="att-iphone18-images" className="block">
            <img
              src="/att/iphone-18-lineup.jpg"
              alt="iPhone 18 Pro and Pro Max lineup in Space Black, Silver White, Sky Blue and Dark Wine"
              className="w-full rounded-3xl transition-transform duration-300 hover:scale-[1.02]"
            />
          </Link>
        </div>
      </div>

      {/* SECTION CARDS */}
      <div className="py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="att-section-cards">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.to} delay={i * 0.04}>
                <Link
                  to={s.to}
                  data-testid={`att-section-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex h-full flex-col rounded-2xl border border-black/10 bg-white p-7 shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: "rgba(0,168,224,0.12)", color: ATT_BLUE }}>
                    <s.Icon size={22} strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-4 font-serif-display text-xl font-bold text-[#1d2329]">{s.label}</h3>
                  <p className="mt-2 font-body font-light text-sm text-[#3c4650] leading-relaxed">{s.desc}</p>
                  <span className="mt-auto pt-4 inline-flex items-center gap-1 font-body text-sm font-bold transition-transform duration-300 group-hover:translate-x-1" style={{ color: ATT_BLUE }}>
                    Explore <ArrowUpRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* PROMOS + SERVICES */}
      <div className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <SectionLabel>Current Promos</SectionLabel>
                <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1d2329]">
                  {tab === "residential" ? "For your home." : "For your business."}
                </h2>
              </div>
              <AudienceToggle value={tab} onChange={setTab} />
            </div>
          </Reveal>

          <PromoGrid audience={tab} />

          <div className="space-y-6">
            {ATT_SERVICES[tab].map((s, i) => (
              <Reveal key={s.id} delay={i * 0.03}>
                <div data-testid={`att-service-${s.id}`} className="rounded-3xl border border-black/10 bg-white p-8 md:p-10 shadow-sm">
                  <h3 className="font-serif-display text-2xl md:text-3xl font-bold text-[#1d2329]">{s.name}</h3>
                  <p className="mt-2 font-body font-light text-base text-[#3c4650]">{s.blurb}</p>
                  <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    {s.details.map((d) => (
                      <li key={d} className="flex items-start gap-3 font-body text-sm md:text-base text-[#3c4650]">
                        <Check size={16} className="mt-1 shrink-0" style={{ color: ATT_BLUE }} /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* OUR AGENCY */}
      <div id="agency" className="scroll-mt-40 py-16 md:py-20 border-t border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="att-agency-section">
          <div className="lg:col-span-7">
            <SectionLabel>Our Agency</SectionLabel>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1d2329]">
              Ordered by people, <span style={{ color: ATT_BLUE }}>not portals.</span>
            </h2>
            <p className="mt-5 font-body font-light text-base md:text-lg text-[#3c4650] leading-relaxed">
              Through our partnership with DM Wireless Connect and AT&amp;T, Heritage Family Protection Agency is an
              AT&amp;T Authorized Dealer. Submit a pre-order and a real person from our team confirms your
              promos, pricing, and availability — we will contact you about your pre-order.
            </p>
            <p className="mt-4 font-body text-sm text-[#3c4650]">
              Questions? Call <a href={CONTACT.phoneHref} className="font-bold" style={{ color: ATT_BLUE }}>{CONTACT.phone}</a> or
              email <a href="mailto:attorders@heritagefamilypa.com" className="font-bold" style={{ color: ATT_BLUE }}>attorders@heritagefamilypa.com</a>.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap items-center gap-5">
            <div className="inline-flex items-center rounded-2xl border border-black/10 bg-white px-6 py-4 shadow-sm">
              <img src={ATT_LOGOS.dm} alt="DM Wireless Connect" className="h-20 w-auto object-contain" />
            </div>
            <div className="inline-flex items-center rounded-2xl border border-black/10 bg-white overflow-hidden shadow-sm">
              <img src="/att/att-authorized-dealer.jpg" alt="AT&T Authorized Dealer" className="h-24 w-auto object-contain" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
